import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Sparkles,
    TrendingUp,
    TrendingDown,
    Lightbulb,
    Users,
    ChevronDown,
    ChevronUp,
} from 'lucide-react'
import { Button } from 'vnetwork-ui'
import Paper from '@/components/Paper'
import { Skeleton } from '@/components/ui/skeleton'
import { QuarterlyMetrics } from '../../../types/quarterly-report'
import {
    analyzeQuarterlyInsights,
    QuarterlyInsightResponse,
} from '../../../services/quarterly-report-gemini'

interface Props {
    data: QuarterlyMetrics[]
    periodRange: string
    onInsightsReady?: (insights: QuarterlyInsightResponse) => void
    onLoadingChange?: (loading: boolean) => void
}

const AiQuarterlyInsights = ({
    data,
    periodRange,
    onInsightsReady,
    onLoadingChange,
}: Props) => {
    const { t } = useTranslation('surveys')
    const [insights, setInsights] = useState<QuarterlyInsightResponse | null>(
        null
    )
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [expanded, setExpanded] = useState(true)
    const hasAutoAnalyzed = useRef(false)

    const handleAnalyze = async (forceRefresh = false) => {
        if (data.length === 0) return
        setLoading(true)
        onLoadingChange?.(true)
        setError(null)
        try {
            const result = await analyzeQuarterlyInsights(
                { data, period_range: periodRange },
                forceRefresh
            )
            setInsights(result)
            onInsightsReady?.(result)
        } catch (err) {
            setError(t('surveys.quarterly_report.ai_error'))
            console.error(err)
        } finally {
            setLoading(false)
            onLoadingChange?.(false)
        }
    }

    // Auto-analyze on first data load
    useEffect(() => {
        if (data.length > 0 && !hasAutoAnalyzed.current && !insights) {
            hasAutoAnalyzed.current = true
            handleAnalyze()
        }
    }, [data])

    if (!insights && !loading) {
        return (
            <Paper className="bg-gradient-to-r from-violet-50 to-blue-50">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-violet-600" />
                        <span className="font-medium text-violet-900">
                            AI Survey Insights
                        </span>
                        <span className="text-sm text-violet-600">
                            Phân tích hài lòng khách hàng
                        </span>
                    </div>
                    <Button
                        size="small"
                        onClick={() => handleAnalyze()}
                        loading={loading}
                        disabled={data.length === 0}
                        className="!bg-violet-600 hover:!bg-violet-700"
                    >
                        <Sparkles className="w-4 h-4 mr-1" />
                        {t('surveys.quarterly_report.ai_analyze')}
                    </Button>
                </div>
                {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            </Paper>
        )
    }

    if (loading) {
        return (
            <Paper className="bg-gradient-to-r from-violet-50 to-blue-50">
                {/* Header skeleton */}
                <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-violet-600 animate-pulse" />
                    <span className="text-violet-900 font-medium">
                        AI Survey Insights
                    </span>
                    <span className="text-sm text-violet-500 animate-pulse">
                        {t('surveys.quarterly_report.ai_analyzing')}
                    </span>
                </div>
                {/* Content skeleton */}
                <div className="space-y-3">
                    <Skeleton className="h-14 w-full rounded-md" />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <Skeleton className="h-20 rounded-md" />
                        <Skeleton className="h-20 rounded-md" />
                        <Skeleton className="h-20 rounded-md" />
                    </div>
                    <Skeleton className="h-24 w-full rounded-md" />
                    <Skeleton className="h-16 w-full rounded-md" />
                </div>
            </Paper>
        )
    }

    if (!insights) return null

    return (
        <Paper className="bg-gradient-to-r from-violet-50 to-blue-50">
            {/* Header */}
            <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-violet-600" />
                    <span className="font-medium text-violet-900">
                        AI Survey Insights
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                            e.stopPropagation()
                            handleAnalyze(true)
                        }}
                        loading={loading}
                        className="!text-violet-600 !border-violet-300"
                    >
                        {t('surveys.quarterly_report.refresh')}
                    </Button>
                    {expanded ? (
                        <ChevronUp className="w-5 h-5 text-violet-600" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-violet-600" />
                    )}
                </div>
            </div>

            {expanded && (
                <div className="mt-4 space-y-4">
                    {/* Summary */}
                    <div className="p-3 bg-white rounded-md">
                        <p className="text-sm text-gray-700">
                            {insights.summary}
                        </p>
                    </div>

                    {/* Customer Health */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-violet-600" />
                            <span className="font-medium text-sm">
                                Sức khỏe khách hàng
                            </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <div className="p-3 bg-white rounded-md">
                                <p className="text-xs font-medium text-red-600 mb-1">
                                    Churn
                                </p>
                                <p className="text-sm text-gray-600">
                                    {insights.customer_health.churn_analysis}
                                </p>
                            </div>
                            <div className="p-3 bg-white rounded-md">
                                <p className="text-xs font-medium text-green-600 mb-1">
                                    Khách mới
                                </p>
                                <p className="text-sm text-gray-600">
                                    {insights.customer_health.acquisition_trend}
                                </p>
                            </div>
                            <div className="p-3 bg-white rounded-md">
                                <p className="text-xs font-medium text-blue-600 mb-1">
                                    Tổng quan
                                </p>
                                <p className="text-sm text-gray-600">
                                    {insights.customer_health.health_overview}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Satisfaction Trends */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-sm">
                                Xu hướng hài lòng
                            </span>
                        </div>
                        <div className="p-3 bg-white rounded-md">
                            <p className="text-sm text-gray-700 mb-2">
                                {insights.satisfaction_trends.overall_trend}
                            </p>
                            {insights.satisfaction_trends.service_areas.length >
                                0 && (
                                <div className="space-y-1.5 mt-2 pt-2 border-t border-gray-100">
                                    {insights.satisfaction_trends.service_areas.map(
                                        (area, i) => (
                                            <div
                                                key={i}
                                                className="flex items-start gap-2"
                                            >
                                                <span className="text-blue-400 mt-0.5 shrink-0">
                                                    •
                                                </span>
                                                <p className="text-sm text-gray-600">
                                                    <span className="font-medium">
                                                        {area.name}
                                                    </span>
                                                    : {area.analysis}
                                                </p>
                                            </div>
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Upsale Opportunities */}
                    {insights.upsale_opportunities.length > 0 && (
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-emerald-600" />
                                <span className="font-medium text-sm">
                                    Cơ hội Upsale
                                </span>
                            </div>
                            <ul className="space-y-1.5">
                                {insights.upsale_opportunities.map((opp, i) => (
                                    <li
                                        key={i}
                                        className="text-sm text-gray-600 flex items-start gap-1"
                                    >
                                        <span className="text-emerald-500 mt-0.5">
                                            •
                                        </span>
                                        {opp}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Key Insights */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <TrendingDown className="w-4 h-4 text-orange-600" />
                            <span className="font-medium text-sm">
                                Insights chính
                            </span>
                        </div>
                        <ul className="space-y-1.5">
                            {insights.key_insights.map((insight, i) => (
                                <li
                                    key={i}
                                    className="text-sm text-gray-600 flex items-start gap-1"
                                >
                                    <span className="text-orange-500 mt-0.5">
                                        •
                                    </span>
                                    {insight}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Comparison Insights */}
                    {insights.comparison_insights && (
                        <div className="p-3 bg-blue-50 rounded-md border border-blue-100">
                            <p className="text-sm font-medium text-blue-700 mb-1">
                                So sánh với kỳ trước
                            </p>
                            <p className="text-sm text-gray-600">
                                {insights.comparison_insights}
                            </p>
                        </div>
                    )}

                    {/* Recommendations */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Lightbulb className="w-4 h-4 text-yellow-600" />
                            <span className="font-medium text-sm">Đề xuất</span>
                        </div>
                        <ul className="space-y-1.5">
                            {insights.recommendations.map((rec, i) => (
                                <li
                                    key={i}
                                    className="text-sm text-gray-600 flex items-start gap-1"
                                >
                                    <span className="text-yellow-500 mt-0.5">
                                        •
                                    </span>
                                    {rec}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </Paper>
    )
}

export default AiQuarterlyInsights
